import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelectedDictionary } from "../../../context/selectedContext";
import type { DictionaryModel } from "../../../type/dictionary";
import type { WordModel } from "../../../type/word";
import { notebookIcon } from "../../../utils/config";
import type { NavigationDropdownModel } from "../../../type/header";
import { fetchWithAuth } from "../../../auth/auth";
import { getApiUrl } from "../../../utils/getApiUrl";
import "./navigationDropdown.scss";

const NavigationDropdown: React.FC<NavigationDropdownModel> = ({ displayTitle }) => {
  const nav = useNavigate();
  const navDropdownRef = useRef<HTMLDivElement>(null);
  const [showNavDropdown, setShowNavDropdown] = useState(false);
  const [dictionaries, setDictionaries] = useState<DictionaryModel[]>([]);
  const {selected, setSelected} = useSelectedDictionary();
  const [wordCounts, setWordCounts] = useState<Record<number, number>>({});

  // Load dictionaries from localStorage and listen to updates
  useEffect(() => {
    try {
      const cached = localStorage.getItem("app_dictionaries");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) setDictionaries(parsed);
      }
    } catch {/* ignore storage errors */}

    const onUpdated = (ev: Event) => {
      const detail = (ev as CustomEvent).detail;
      if (Array.isArray(detail)) setDictionaries(detail);
    };
    document.addEventListener("dictionaries:updated", onUpdated as EventListener);

    return () => {
      document.removeEventListener("dictionaries:updated", onUpdated as EventListener);
    };
  }, []);

  // Fetch word counts when dictionaries change or a refresh event fires
  useEffect(() => {
    if (dictionaries.length === 0) return;

    const fetchCounts = async () => {
      const counts: Record<number, number> = {};
      await Promise.all(
        dictionaries.map(async (dict) => {
          try {
            const res = await fetchWithAuth(getApiUrl(`words?iddictionary=${dict.iddictionary}`));
            if (!res.ok) return;
            const data: WordModel[] = await res.json();
            counts[dict.iddictionary] = Array.isArray(data) ? data.filter((w) => !w.deleted).length : 0;
          } catch {
            /* ignore */
          }
        })
      );
      setWordCounts(counts);
    };

    fetchCounts();

    document.addEventListener("wordcounts:refresh", fetchCounts);
    return () => document.removeEventListener("wordcounts:refresh", fetchCounts);
  }, [dictionaries]);

  const toggleNavDropdown = () => setShowNavDropdown(!showNavDropdown);

  const handleNavigateToDictionaries = () => {
    nav("/dictionaries");
    setShowNavDropdown(false);
  };

  const handleNavigateToAllWords = () => {
    setSelected(null);
    if (window.location.pathname === "/flashcards") {
      nav("/flashcards?name=All Expressions");
    } else {
      nav("/words");
    }
    setShowNavDropdown(false);
  };

  const handleNavigateToDictionary = (dict: DictionaryModel) => {
    setSelected({ id: dict.iddictionary, name: dict.name });
    if (window.location.pathname === "/flashcards") {
      nav("/flashcards?iddictionary=" + dict.iddictionary + "&name=" + encodeURIComponent(dict.name));
    } else {
      nav("/words?iddictionary=" + dict.iddictionary);
    }
    setShowNavDropdown(false);
  };

  const handleNavigateToNotebook = () => {
    setSelected(null);
    nav("/notebook");
    setShowNavDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target as Node)) {
        setShowNavDropdown(false);
      }
    };
    if (showNavDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNavDropdown]);

  return (
    <div className="header-nav-dropdown-container" ref={navDropdownRef}>
      <button 
        className="header-nav-dropdown-btn"
        onClick={toggleNavDropdown}
        title="Navigation menu"
        aria-label="Navigation menu"
      >
        <span className="header-nav-dropdown-title">{displayTitle}</span>
        <i className={`bi bi-chevron-${showNavDropdown ? 'up' : 'down'}`} style={{ fontSize: "0.875rem", marginLeft: "0.5rem" }}></i>
      </button>

      {showNavDropdown && (
        <div className="header-nav-dropdown">
          <div className="header-nav-dropdown-section">
            <button 
              className={`header-nav-dropdown-item ${window.location.pathname === "/dictionaries" ? "active" : ""}`}
              onClick={handleNavigateToDictionaries}
            >
              <i className="bi bi-book" style={{ fontSize: "1rem" }}></i>
              Dictionaries
            </button>

            {dictionaries.length > 0 && (
              <div className="header-nav-dropdown-subitems">
                <button
                  className={`header-nav-dropdown-subitem ${window.location.pathname === "/words" && !selected ? "active" : ""}`}
                  onClick={handleNavigateToAllWords}
                  title="All Expressions"
                >
                  <i className="bi bi-book-fill" style={{ fontSize: "0.875rem" }}></i>
                  <span className="header-nav-dropdown-subitem-text">All Expressions</span>
                </button>
                {dictionaries.map((dict) => (
                  <button
                    key={dict.iddictionary}
                    className={`header-nav-dropdown-subitem ${(window.location.pathname === "/words" || window.location.pathname === "/flashcards") && selected?.id === dict.iddictionary ? "active" : ""}`}
                    onClick={() => handleNavigateToDictionary(dict)}
                    title={dict.name}
                  >
                    <i className="bi bi-book-fill" style={{ fontSize: "0.875rem" }}></i>
                    <span className="header-nav-dropdown-subitem-text">{dict.name}</span>
                    {wordCounts[dict.iddictionary] !== undefined && (
                      <span className="header-nav-dropdown-subitem-count">
                        {wordCounts[dict.iddictionary]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button 
            className={`header-nav-dropdown-item ${window.location.pathname === "/notebook" ? "active" : ""}`}
            onClick={handleNavigateToNotebook}
          >
            {notebookIcon} Notebook
          </button>

          <div className="header-dropdown-divider"></div>

        </div>
      )}
    </div>
  );
};

export default NavigationDropdown;